import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Heading from '../../../generic-components/Heading';
import SendMoneyWizardFooter from '../components/SendMoneyWizardFooter';
import TransferDetailsReview from '../components/TransferDetailsReview';
import PayeeDetailsReview from '../components/PayeeDetailsReview';
import TransferTypesPicker from '../components/TransferTypesPicker';
import { SendMoneyContext } from './SendMoney';
import { trackMixPanel } from '../../../services/mixpanel.service';

const STYLES = {
  WRAPPER: 'flex flex-col items-center justify-center w-full h-[calc(100vh-160px)] overflow-y-auto mt-[58px] bg-bw00',
  MAIN: 'w-full flex flex-col items-center h-full justify-center',
  CONTAINER: 'relative flex flex-col w-[464px] h-full rounded-[8px] bg-bw00 pt-[32px]',
  MAIN_HEAD: 'font-metricR text-[28px] text-bw08 text-center',
  REVIEW_CONTAINER: 'rounded-[8px] p-[16px] border-[0.8px] border-bw03 mt-[44px] mb-[16px]',
  UNDERLINE: 'w-full h-[0.8px] bg-bw03 my-[16px]',
  DISCLAIMER: 'font-metricR text-sm text-bw06 leading-5 mt-[8px] mb-[8px]',
};

interface ITransferReviewPageProps {
  onSubmit: () => void;
  onEditAmount: () => void;
  onEditPayee: () => void;
}

const TransferReviewPage = ({ onSubmit, onEditAmount, onEditPayee }: ITransferReviewPageProps) => {
  const { t } = useTranslation();
  const { selectedLinkedAccount, amount, setChosenFee, chosenFee, isSendMoneyLoading } = useContext(SendMoneyContext);

  const {
    institutionName = '',
    accountId = '',
    routingNumber = '',
    accountName = '',
    mask = '',
  } = selectedLinkedAccount || {};

  const showPayeeDetailsReview = institutionName && accountId && routingNumber && accountName && mask;
  const isConfirmEnabled = chosenFee !== null && amount && selectedLinkedAccount !== null;
  useEffect(() => {
    trackMixPanel('View Move Money Review Screen', { Flow: 'plaid transfer out' });
  }, []);
  const handleEditAmountClick = () => {
    trackMixPanel('Act on Move Money Review Screen', { Flow: 'plaid transfer out', Action: 'Edit Amount' });
    onEditAmount();
  };
  const handleEditPayeeClick = () => {
    trackMixPanel('Act on Move Money Review Screen', { Flow: 'plaid transfer out', Action: 'Edit Payee' });
    onEditPayee();
  };
  const handleContinuePayeeClick = () => {
    trackMixPanel('Act on Move Money Review Screen', { Flow: 'plaid transfer out', Action: 'Continue' });
    if (isConfirmEnabled) onSubmit();
  };

  return (
    <div className={STYLES.WRAPPER}>
      <div className={STYLES.MAIN}>
        <div className={STYLES.CONTAINER}>
          <Heading title={t('ACH_OUT_REVIEW.TITLE')} className={STYLES.MAIN_HEAD} />
          <div className={STYLES.REVIEW_CONTAINER}>
            <TransferDetailsReview amount={amount} onEditAmountClick={handleEditAmountClick} />
            <div className={STYLES.UNDERLINE} />

            {showPayeeDetailsReview && (
              <PayeeDetailsReview
                instituteName={institutionName}
                accountDetails={`${accountName} ${mask}`}
                accountNumber={accountId}
                routingNumber={routingNumber}
                onEditPayeeDetailsClick={handleEditPayeeClick}
              />
            )}
          </div>
          <TransferTypesPicker transferAmount={amount} setChosenFeeData={setChosenFee} />
          <p data-testid='SendMoney-review-disclaimer' className={STYLES.DISCLAIMER}>
            {t('SEND_MONEY.REVIEW.TRANSFER_DISCLAIMER')}
          </p>
        </div>
      </div>
      <SendMoneyWizardFooter
        className='w-[375px]'
        title={t('SEND_MONEY.WIZARD_FOOTER_BUTTONS.REVIEW_STEP')}
        disabled={!isConfirmEnabled}
        onClick={handleContinuePayeeClick}
        isLoading={isSendMoneyLoading}
      />
    </div>
  );
};

export default TransferReviewPage;
